import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PhoneInput from '../../components/PhoneInput/PhoneInput';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { createEmptyPhoneModel, toPhonePayload } from '../../utils/phoneCountries';
import styles from './loginparticipantepage.module.css';

function getFlowInfo(location) {
  const from = location?.state?.from || null;
  const match = (from?.pathname || '').match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const params = new URLSearchParams(location.search || '');
  const queryDest = (params.get('dest') || '').toLowerCase();
  const matchedDest = match?.[1]?.toLowerCase().startsWith('ead') ? 'ead' : (match ? 'res' : '');

  return {
    from,
    turma: (params.get('turma') || '').trim() || match?.[2] || '',
    dest: queryDest === 'res' || queryDest === 'ead' ? queryDest : matchedDest,
  };
}

function getRedirectPath(flow) {
  if (flow.from?.pathname) {
    return `${flow.from.pathname}${flow.from.search || ''}${flow.from.hash || ''}`;
  }
  if (flow.turma && flow.dest) return `/${flow.dest}/${encodeURIComponent(flow.turma)}`;
  return '/participante/page';
}

function LoginParticipantePage() {
  const { isAuthenticated, loginParticipante } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const flow = useMemo(() => getFlowInfo(location), [location]);
  const [telefone, setTelefone] = useState(() => createEmptyPhoneModel());
  const [senha, setSenha] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [telefoneErro, setTelefoneErro] = useState('');

  const cadastroSearch = useMemo(() => {
    const params = new URLSearchParams();
    if (flow.turma) params.set('turma', flow.turma);
    if (flow.dest) params.set('dest', flow.dest);
    const search = params.toString();
    return search ? `?${search}` : '';
  }, [flow.dest, flow.turma]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErro('');
    setTelefoneErro('');

    if (!telefone.isValid) {
      const message = t('phoneCountry.invalid');
      setTelefoneErro(message);
      showToast(message, 3000, 'error');
      return;
    }

    setCarregando(true);
    try {
      const result = await loginParticipante({
        ...toPhonePayload('celular', telefone),
        senha,
      });

      if (result.success) {
        showToast(t('auth.loginSuccess'), 3000, 'success');
        navigate(getRedirectPath(flow), { replace: true });
        return;
      }

      const message = result.status === 401
        ? t('auth.invalidCredentials')
        : (result.message || t('auth.loginFailed'));
      setErro(message);
      showToast(message, 3000, 'error');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate(getRedirectPath(flow), { replace: true });
  }, [flow, isAuthenticated, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('auth.loginTitle')}</h2>
      <form onSubmit={handleSubmit} className={styles.form}>
        <p>{t('auth.phoneIntro')}</p>
        <PhoneInput
          ref={inputRef}
          value={telefone}
          onChange={setTelefone}
          label={t('phoneCountry.cellphoneLabel')}
          error={telefoneErro}
          disabled={carregando}
          required
        />
        <input
          type="password"
          placeholder={t('auth.password')}
          value={senha}
          onChange={(event) => setSenha(event.target.value)}
          className={styles.input}
          required
        />
        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? t('auth.entering') : t('auth.enter')}
        </button>
        {erro && <p className={styles.error}>{erro}</p>}
        <Link to="/participante/recuperar" className={styles.link}>{t('auth.forgotPassword')}</Link>
        <p className={styles.linkText}>
          {t('auth.noAccess')}{' '}
          <Link
            to={`/participante/cadastro${cadastroSearch}`}
            state={{ from: flow.from }}
            className={styles.link}
          >
            {t('auth.signUpLink')}
          </Link>
        </p>
      </form>
    </div>
  );
}

export default LoginParticipantePage;
